import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ChevronDown, WholeWord } from 'lucide-react';
interface FilterDropdownProps {
  columnKey: string;
  title: string;
  placeholder: string;
  allItems: string[];
  isActive: boolean;
  hasFilters: boolean;
  currentSelected: Set<string>;
  searchText: string;
  setSearchText: (text: string) => void;
  onToggle: () => void;
  onToggleItem: (value: string) => void;
  onSelectAll: (isAllSelected: boolean, filteredItems: string[]) => void;
  onApply: () => void;
  onCancel: () => void;
  dropdownRef: (el: HTMLDivElement | null) => void;
}
export function FilterDropdown({
  columnKey,
  title,
  placeholder,
  allItems,
  isActive,
  hasFilters,
  currentSelected,
  searchText,
  setSearchText,
  onToggle,
  onToggleItem,
  onSelectAll,
  onApply,
  onCancel,
  dropdownRef,
}: FilterDropdownProps) {
  const [isComposing, setIsComposing] = useState(false);
  const [localValue, setLocalValue] = useState(searchText || '');
  const [scrollTop, setScrollTop] = useState(0);
  const [isFullMatchMode, setIsFullMatchMode] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  // Virtual scrolling constants
  const ITEM_HEIGHT = 32; // Height of each item
  const CONTAINER_HEIGHT = 384; // max-h-96 = 384px
  const VISIBLE_ITEMS = Math.ceil(CONTAINER_HEIGHT / ITEM_HEIGHT);
  useEffect(() => {
    if (!isComposing) {
      setLocalValue(searchText || '');
    }
  }, [searchText, isComposing]);
  // 基本过滤逻辑
  const effectiveSearchText = isComposing ? localValue : searchText;
  // 合并可选项和已选中项（已选中但不在可选项中的也要显示）
  const allAvailableItems = useMemo(() => {
    const availableSet = new Set(allItems);
    const selectedSet = new Set(currentSelected);
    // 将已选中但不在当前可选项中的项目也加入到显示列表
    const combinedItems = new Set([...allItems]);
    selectedSet.forEach(item => {
      if (!availableSet.has(item)) {
        combinedItems.add(item);
      }
    });
    return Array.from(combinedItems).sort();
  }, [allItems, currentSelected]);
  const filteredItems = effectiveSearchText
    ? allAvailableItems.filter((item: string) => {
        const searchLower = effectiveSearchText.toLowerCase();
        const itemLower = item.toLowerCase();
        if (isFullMatchMode) {
          // Full match mode: only exact substring matching
          return itemLower.includes(searchLower);
        } else {
          // Subsequence mode (default): check if search text is a subsequence
          if (itemLower.includes(searchLower)) return true;
          let searchIndex = 0;
          for (
            let i = 0;
            i < itemLower.length && searchIndex < searchLower.length;
            i++
          ) {
            if (itemLower[i] === searchLower[searchIndex]) {
              searchIndex++;
            }
          }
          return searchIndex === searchLower.length;
        }
      })
    : allAvailableItems;
  const isAllSelected =
    filteredItems.length > 0 &&
    filteredItems.every((item: string) => currentSelected.has(item));
  // Virtual scrolling calculations
  const virtualScrollData = useMemo(() => {
    const totalItems = filteredItems.length;
    const totalHeight = totalItems * ITEM_HEIGHT;
    if (totalItems === 0 || filteredItems.length <= 50) {
      return {
        totalHeight: 0,
        visibleItems: filteredItems,
        offsetY: 0,
        startIndex: 0,
        endIndex: totalItems,
        useVirtualScrolling: false,
      };
    }
    // Calculate visible range for virtual scrolling
    const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT));
    const endIndex = Math.min(startIndex + VISIBLE_ITEMS + 2, totalItems); // +2 for buffer
    // Get visible items
    const visibleItems = filteredItems.slice(startIndex, endIndex);
    // Calculate offset for positioning
    const offsetY = startIndex * ITEM_HEIGHT;
    return {
      totalHeight,
      visibleItems,
      offsetY,
      startIndex,
      endIndex,
      useVirtualScrolling: true,
    };
  }, [filteredItems, scrollTop, ITEM_HEIGHT, VISIBLE_ITEMS]);
  // Reset scroll when query changes
  useEffect(() => {
    setScrollTop(0);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [searchText]);
  // Handle scroll event for virtual scrolling
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    setScrollTop(target.scrollTop);
  }, []);
  const handleCompositionStart = () => setIsComposing(true);
  const handleCompositionEnd = (
    e: React.CompositionEvent<HTMLInputElement>
  ) => {
    setIsComposing(false);
    const value = e.currentTarget.value;
    setLocalValue(value);
    setSearchText(value);
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalValue(value);
    if (!isComposing) {
      setSearchText(value);
    }
  };
  return (
    <div className="flex items-center justify-between">
      <span>{title}</span>
      <div className="relative">
        <button
          onClick={onToggle}
          className={`ml-1 p-1 rounded hover:bg-gray-200 transition-colors ${
            hasFilters ? 'text-blue-600' : 'text-gray-400'
          }`}
        >
          <ChevronDown size={18} />
        </button>
        {isActive && (
          <div
            ref={dropdownRef}
            className="absolute top-full right-0 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
          >
            <div className="p-3 border-b border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  placeholder={placeholder}
                  value={localValue}
                  onChange={handleInputChange}
                  onCompositionStart={handleCompositionStart}
                  onCompositionEnd={handleCompositionEnd}
                  className="flex-1 px-3 py-1.5 border border-gray-400 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-100 focus:border-blue-100 font-normal placeholder:text-xs"
                  autoFocus
                />
                <button
                  onClick={() => setIsFullMatchMode(!isFullMatchMode)}
                  className={`p-1.5 rounded transition-colors ${
                    isFullMatchMode
                      ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                      : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                  }`}
                  title={
                    isFullMatchMode
                      ? 'Full Match Mode Enabled'
                      : 'Full Match Mode Disabled'
                  }
                >
                  <WholeWord size={14} />
                </button>
              </div>
            </div>
            {filteredItems.length > 0 && (
              <div className="border-b border-gray-200">
                <div
                  className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-xs flex items-center font-normal"
                  onClick={() => onSelectAll(isAllSelected, filteredItems)}
                >
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    readOnly
                    className="mr-2 h-3 w-3"
                  />
                  <span className="font-medium text-blue-600">
                    {isAllSelected ? 'Deselect All' : 'Select All'} (
                    {filteredItems.length})
                  </span>
                </div>
              </div>
            )}
            <div
              className="max-h-96 overflow-y-auto"
              ref={listRef}
              onScroll={
                virtualScrollData.useVirtualScrolling ? handleScroll : undefined
              }
            >
              {virtualScrollData.useVirtualScrolling ? (
                <div
                  style={{
                    height: `${virtualScrollData.totalHeight}px`,
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      transform: `translateY(${virtualScrollData.offsetY}px)`,
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                    }}
                  >
                    {virtualScrollData.visibleItems.map(
                      (value: string, index: number) => {
                        const actualIndex =
                          virtualScrollData.startIndex + index;
                        const isAvailable = allItems.includes(value); // 检查是否在当前可用项中
                        return (
                          <div
                            key={`${columnKey}-${value}-${actualIndex}`}
                            className={`px-3 py-2 hover:bg-gray-50 text-xs flex items-center font-normal ${
                              !isAvailable ? 'bg-gray-100' : ''
                            }`}
                            title={value}
                            style={{ height: `${ITEM_HEIGHT}px` }}
                          >
                            <input
                              type="checkbox"
                              checked={currentSelected.has(value)}
                              onChange={_ => {
                                const searchInput = document.querySelector(
                                  `input[placeholder*="${placeholder}"]`
                                ) as HTMLInputElement;
                                if (searchInput) searchInput.blur();
                                onToggleItem(value);
                              }}
                              className="mr-2 h-3 w-3 cursor-pointer"
                            />
                            <span
                              className={`truncate flex-1 cursor-pointer ${
                                !isAvailable ? 'text-gray-500 italic' : ''
                              }`}
                              onClick={() => {
                                const searchInput = document.querySelector(
                                  `input[placeholder*="${placeholder}"]`
                                ) as HTMLInputElement;
                                if (searchInput) searchInput.blur();
                                onToggleItem(value);
                              }}
                            >
                              {value}
                              {!isAvailable && ' (selected but not available)'}
                            </span>
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
              ) : (
                virtualScrollData.visibleItems.map(
                  (value: string, index: number) => {
                    const isAvailable = allItems.includes(value); // 检查是否在当前可用项中
                    return (
                      <div
                        key={`${columnKey}-${value}-${index}`}
                        className={`px-3 py-2 hover:bg-gray-50 text-xs flex items-center font-normal ${
                          !isAvailable ? 'bg-gray-100' : ''
                        }`}
                        title={value}
                      >
                        <input
                          type="checkbox"
                          checked={currentSelected.has(value)}
                          onChange={_ => {
                            const searchInput = document.querySelector(
                              `input[placeholder*="${placeholder}"]`
                            ) as HTMLInputElement;
                            if (searchInput) searchInput.blur();
                            onToggleItem(value);
                          }}
                          className="mr-2 h-3 w-3 cursor-pointer"
                        />
                        <span
                          className={`truncate flex-1 cursor-pointer ${
                            !isAvailable ? 'text-gray-500 italic' : ''
                          }`}
                          onClick={() => {
                            const searchInput = document.querySelector(
                              `input[placeholder*="${placeholder}"]`
                            ) as HTMLInputElement;
                            if (searchInput) searchInput.blur();
                            onToggleItem(value);
                          }}
                        >
                          {value}
                          {!isAvailable && ' (selected but not available)'}
                        </span>
                      </div>
                    );
                  }
                )
              )}
            </div>
            <div className="p-3 border-b border-gray-200">
              <div className="flex gap-2 mt-2">
                <button
                  onClick={onApply}
                  className="flex-1 px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors font-normal"
                >
                  Ok
                </button>
                <button
                  onClick={onCancel}
                  className="flex-1 px-3 py-1.5 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300 transition-colors font-normal"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
